import React, { useState } from "react";
import { NavLink, useRouteMatch, Link } from "react-router-dom";
import styled, { theme, css } from "../styled";
import { Text } from "../design-system/components/Text";
import { LinkButton } from "../design-system/components/LinkButton";
import { timestampSeconds, daysAgo } from "../utils/date";
import { useGetPatientTabsQuery } from "../graphql/hooks/useGetPatientTabsQuery";
import { useLogoutMutation } from "../graphql/hooks/useLogoutMutation";
import { reset, trackEvent } from "../analytics";
import { rebootIntercom } from "../intercom";
import DeprecatedErrorModal from "./DeprecatedErrorModal";
import Divider from "./Divider";
import Icon from "./Icon";
import { Logo } from "../design-system/components/Logo";
import {
  CapsuleIcon,
  HeadsetMicIcon,
  AccountCircleIcon,
  ExitToAppIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  MenuIcon,
  ClearIcon,
  CampaignIcon,
  FavoriteBorderIcon,
  ShoppingBagIcon
} from "@echo-health/icons-web";
import { VisuallyHidden } from "../design-system/components/VisuallyHidden";
import { Button } from "../design-system/components/Button";
import { Avatar } from "../design-system/components/Avatar";
import { WhatsNewBadge } from "./WhatsNewBadge";
import UnreadIndicator from "./UnreadIndicator";

export const DESKTOP_NAVIGATION_WIDTH = "240px";

const NavigationLayout = styled.nav<{ isMobileNavExpanded: boolean }>`
  width: 100%;
  min-height: ${p => (p.isMobileNavExpanded ? "100vh" : "auto")};
  background: ${p => p.theme.color.white};
  box-shadow: ${p => p.theme.shadow.depth1};
  overflow: auto;

  @media screen and (min-width: ${theme.breakpoints.m}) {
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${DESKTOP_NAVIGATION_WIDTH};
  }
`;

const NavigationBar = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: ${p => p.theme.spacing.s};
  padding: ${p => p.theme.spacing.m};
  align-items: center;

  @media screen and (min-width: ${theme.breakpoints.m}) {
    grid-template-columns: 1fr;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;

  @media screen and (min-width: ${theme.breakpoints.m}) {
    margin: ${p => p.theme.spacing.m} 0;
  }
`;

const MenuToggleButton = styled(Button)`
  @media screen and (min-width: ${theme.breakpoints.m}) {
    display: none;
  }
`;

const NavigationContent = styled.div<{ isMobileNavExpanded: boolean }>`
  display: ${p => (p.isMobileNavExpanded ? "grid" : "none")};
  grid-template-columns: 1fr;
  grid-gap: ${p => p.theme.spacing.s};
  padding: ${p => p.theme.spacing.m};
  padding-top: 0;

  @media screen and (min-width: ${theme.breakpoints.m}) {
    display: grid;
    padding: ${p => `${p.theme.spacing.xl} ${p.theme.spacing.m}`};
    padding-top: 0;
  }
`;

const navigationButtonBaseStyle = css`
  display: grid;
  grid-template-columns: 24px 1fr max-content;
  grid-gap: ${p => p.theme.spacing.s};
  align-items: center;
  text-decoration: none;
  padding: ${p => p.theme.spacing.m};
  margin-left: ${p => `-${p.theme.spacing.m}`};
  margin-right: ${p => `-${p.theme.spacing.m}`};
  transition: background-color 250ms ease-in-out, box-shadow 250ms ease-in-out;
  outline: none;

  &:hover {
    background-color: ${p => p.theme.color.gray10};
  }

  &:focus:not(:hover) {
    background-color: ${p => p.theme.color.blue10};
    box-shadow: ${p => `${p.theme.shadow.focusRing} ${p.theme.color.blue20}`};
  }

  @media screen and (min-width: ${theme.breakpoints.m}) {
    padding: ${p => p.theme.spacing.s};
    margin-left: ${p => `-${p.theme.spacing.s}`};
    margin-right: ${p => `-${p.theme.spacing.s}`};
    border-radius: ${p => p.theme.spacing.xl};
  }
`;

const navigationLinkBaseStyles = css`
  /* Icon */
  & :nth-child(1) {
    color: ${p => p.theme.color.gray40};
  }

  /* Text */
  & :nth-child(2) {
    color: ${p => p.theme.color.gray70};
  }

  /* Active state */
  &.active {
    background-color: ${p => p.theme.color.blue10};
    & :nth-child(1) {
      color: ${p => p.theme.color.blue60};
    }
    & :nth-child(2) {
      color: ${p => p.theme.color.blue70};
      font-weight: ${p => p.theme.typography.weight.bold};
    }
  }
`;

const NavigationButton = styled.button`
  ${navigationButtonBaseStyle};
  ${navigationLinkBaseStyles};
`;

const NavigationLink = styled(NavLink)`
  ${navigationButtonBaseStyle};
  ${navigationLinkBaseStyles};
`;

const ExternalNavigationLink = styled.a`
  ${navigationButtonBaseStyle};
  ${navigationLinkBaseStyles};
`;

const PatientLink = styled(NavLink)`
  ${navigationButtonBaseStyle};
`;

const PatientListToggle = styled.button<{ isOpen: boolean }>`
  ${navigationButtonBaseStyle};
  ${p =>
    p.isOpen &&
    css`
      background-color: ${p => p.theme.color.blue10};
    `}
`;

const NameText = styled(Text)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ItemText = styled(Text).attrs({ nonSensitive: true })`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Badge = styled.div`
  background: ${p => p.theme.color.purple60};
  color: ${p => p.theme.color.white};
  font-size: ${p => p.theme.typography.size.xs};
  font-weight: ${p => p.theme.typography.weight.bold};
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.s};
  display: flex;
  align-items: center;
  text-align: center;
  border-radius: ${p => p.theme.spacing.l};
`;

export function PrimaryNav() {
  const { params, path } = useRouteMatch<{ patientId: string }>();
  const patientId = params.patientId;

  // Keep track of the selected patient in state so you stay on the
  // same patient when you visit a link without the `patientId` param
  // e.g. medicine (with param) -> account (without param)
  const [selectedPatientId, setSelectedPatientId] = useState(patientId);

  const [isPatientPickerExpanded, setIsPatientPickerExpanded] = useState(false);
  const [isMobileNavExpanded, setIsMobileNavExpanded] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const { data, loading, error } = useGetPatientTabsQuery({
    variables: { updateTimeAfter: timestampSeconds(daysAgo(7)) }
  });

  const [logout, { error: logoutError }] = useLogoutMutation({
    onError: () => {
      return setIsErrorModalOpen(true);
    },
    onCompleted: response => {
      if (!response) return;
      trackEvent("nav_logout", {
        patientid: patientId
      });
      if (response.logout.errors.length > 0) {
        setIsErrorModalOpen(true);
        return;
      } else {
        reset();
        rebootIntercom();
        /**
         * Instead of pushing client side via react-router
         * we are going to reload the page so that we clear
         * the Apollo client side cache and redux.
         */
        window.location.href = "/login";
      }
    }
  });

  if (loading || error || !data || !data.account) {
    return null;
  }

  const isReferralsEnabled =
    data.features.length > 0 &&
    !!data.features.find(flag => flag.name === "referrals" && flag.isEnabled);

  const hasRedeemableBalance =
    !!data.account.referrals.redeemableBalance &&
    data.account.referrals.redeemableBalance > 0;

  const { patients } = data.account;

  const accountHolder = patients.edges.find(i => i.node.isAccountHolder);

  const selectedPatient =
    patients.edges.find(i => i.node.id === selectedPatientId) || accountHolder;

  const patientList = selectedPatient
    ? patients.edges.filter(i => i.node.id !== selectedPatient.node.id)
    : patients.edges;

  function onNavigate() {
    // Close the mobile nav and patient picker
    setIsMobileNavExpanded(false);
    setIsPatientPickerExpanded(false);
  }

  return (
    <>
      <NavigationLayout isMobileNavExpanded={isMobileNavExpanded}>
        <NavigationBar>
          <LogoLink to="/">
            <Logo />
          </LogoLink>
          <MenuToggleButton
            label={isMobileNavExpanded ? "Close" : "Menu"}
            iconLeading={isMobileNavExpanded ? ClearIcon : MenuIcon}
            variant="secondary"
            size="s"
            onClick={() => {
              setIsMobileNavExpanded(!isMobileNavExpanded);
            }}
          />
        </NavigationBar>

        <NavigationContent isMobileNavExpanded={isMobileNavExpanded}>
          <Divider margin="0 0 s 0" />
          {selectedPatient ? (
            <>
              <VisuallyHidden id="patientToggleDesc">
                Switch or add new patients
              </VisuallyHidden>
              <PatientListToggle
                aria-describedby="patientToggleDesc"
                isOpen={isPatientPickerExpanded}
                onClick={() => {
                  if (!isPatientPickerExpanded) {
                    trackEvent("patients_list_open", {
                      patientid: patientId
                    });
                  }

                  setIsPatientPickerExpanded(!isPatientPickerExpanded);
                }}
              >
                <Avatar
                  name={`${selectedPatient.node.firstName} ${selectedPatient.node.lastName}`}
                  aria-hidden={true}
                />
                <NameText>
                  {selectedPatient.node.firstName}{" "}
                  {selectedPatient.node.lastName}
                </NameText>
                <Icon
                  type={
                    isPatientPickerExpanded
                      ? KeyboardArrowUpIcon
                      : KeyboardArrowDownIcon
                  }
                />
              </PatientListToggle>
              {isPatientPickerExpanded ? (
                <>
                  {patientList.map(edge => {
                    const patient = edge.node;

                    const activeOrdersTotal =
                      patient.activeOrders.totalCount +
                      patient.recentHistoryOrders.totalCount;

                    return (
                      <PatientLink
                        key={patient.id}
                        to={path.replace(":patientId", patient.id)}
                        onClick={() => {
                          setSelectedPatientId(patient.id);
                          trackEvent("patient_chosen", {
                            patientid: patientId
                          });
                          onNavigate();
                        }}
                      >
                        <Avatar
                          name={`${patient.firstName} ${patient.lastName}`}
                          aria-hidden={true}
                        />
                        <ItemText>
                          {patient.firstName} {patient.lastName}
                        </ItemText>
                        {activeOrdersTotal > 0 ? (
                          <Badge>{activeOrdersTotal}</Badge>
                        ) : null}
                      </PatientLink>
                    );
                  })}
                  <Text size="s" color="gray70" margin="xs 0">
                    Manage medicine for other people by adding them here.
                  </Text>
                  <LinkButton
                    variant="secondary"
                    label="Add another person"
                    linkTo="/account/add-patient"
                    onClick={() => {
                      onNavigate();
                    }}
                  />
                </>
              ) : null}
              <Divider margin="s 0" />
            </>
          ) : null}

          {/*
            Hide the following links if there is no patient.
            Usually means the account isn't complete.
         */}
          {selectedPatient ? (
            <>
              <NavigationLink
                to={`/patient/${selectedPatient.node.id}/medication`}
                onClick={() => {
                  trackEvent("nav_medication");
                  onNavigate();
                }}
              >
                <Icon type={CapsuleIcon} />
                <ItemText>Medicine</ItemText>
              </NavigationLink>
            </>
          ) : null}

          <NavigationLink
            to={selectedPatient ? `/help/${selectedPatient.node.id}` : "/help"}
            onClick={() => {
              trackEvent("nav_help");
              onNavigate();
            }}
          >
            <Icon type={HeadsetMicIcon} />
            <ItemText>Help</ItemText>
          </NavigationLink>

          <NavigationLink
            to="/account"
            onClick={() => {
              trackEvent("nav_account");
              onNavigate();
            }}
          >
            <Icon type={AccountCircleIcon} />
            <ItemText>Account</ItemText>
          </NavigationLink>

          <Divider margin="s 0" />

          {isReferralsEnabled && (
            <>
              <NavigationLink
                to="/invite-a-friend"
                onClick={() => {
                  trackEvent("nav_referrals");
                  onNavigate();
                }}
              >
                <Icon type={FavoriteBorderIcon} />
                <ItemText>Invite a friend</ItemText>
                {hasRedeemableBalance && <UnreadIndicator />}
              </NavigationLink>

              <ExternalNavigationLink
                href="https://lloydspharmacy.com/?utm_source=echo&utm_medium=shop&utm_campaign=referral"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackEvent("nav_lloyds_shop");
                  onNavigate();
                }}
              >
                <Icon type={ShoppingBagIcon} />
                <ItemText>Shop</ItemText>
              </ExternalNavigationLink>
            </>
          )}

          <NavigationLink
            to="/whats-new"
            onClick={() => {
              trackEvent("nav_whats_new");
              onNavigate();
            }}
          >
            <Icon type={CampaignIcon} />
            <ItemText>What’s new</ItemText>
            <WhatsNewBadge />
          </NavigationLink>

          <NavigationButton
            onClick={() => {
              logout();
            }}
          >
            <Icon type={ExitToAppIcon} />
            <ItemText>Log out</ItemText>
          </NavigationButton>
        </NavigationContent>
      </NavigationLayout>
      <DeprecatedErrorModal
        isOpen={isErrorModalOpen}
        error={logoutError}
        onDismiss={() => setIsErrorModalOpen(false)}
      />
    </>
  );
}
